import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Edit, Home, Plus, History, Hexagon, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import ApiaryMap from '../ApiaryMap';
import SaveApiaryOfflineButton from './SaveApiaryOfflineButton';

const ApiariesList = ({ yards = [], hives = [], onEdit, onSelect, onAddApiary, onShowHistory }) => {
  const navigate = useNavigate();
  const getHiveCount = (yardName) => hives.filter(h => h.yard === yardName).length;

  return (
    <div className="space-y-6" data-testid="apiaries-list">
      <ApiaryMap yards={yards} hives={hives} onSelectYard={onSelect} />

      <div className="bg-white rounded-xl border border-stone-200 shadow-sm">
        <div className="p-3 sm:p-4 border-b border-stone-100 flex justify-between items-center gap-2">
          <h2 className="font-bold text-black text-base sm:text-lg">Apiaries ({yards.length})</h2>
          <Button onClick={onAddApiary} className="bg-teal-500 hover:bg-teal-600 text-white text-xs sm:text-sm px-2 sm:px-4" data-testid="add-apiary-btn">
            <Plus className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Add Apiary</span>
          </Button>
        </div>

        <div className="p-3 sm:p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {yards.length === 0 ? (
            <div className="col-span-full text-center py-8 text-stone-500">No apiaries yet. Add your first apiary to get started!</div>
          ) : (
            yards.map(yard => {
              const count = getHiveCount(yard.name);
              return (
                <div
                  key={yard.id}
                  className="bg-white rounded-xl shadow-sm border-l-4 border-l-teal-400 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => onSelect?.(yard.name)}
                >
                  {yard.image_url && (
                    <img src={yard.image_url} alt={yard.name} className="w-full h-24 object-cover rounded-tr-xl" />
                  )}
                  <div className="p-3 flex gap-3">
                    <div className="w-11 h-11 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                      <Hexagon className="h-5 w-5 text-teal-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0">
                          <h4 className="font-bold text-sm text-slate-800 truncate leading-tight">{yard.name}</h4>
                          {yard.location && (
                            <div className="flex items-center gap-1 text-[11px] text-slate-500 mt-0.5">
                              <MapPin className="h-3 w-3 text-teal-400 flex-shrink-0" />
                              <span className="truncate">{yard.location}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={(e) => { e.stopPropagation(); onShowHistory?.(yard); }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600" title="History">
                            <History className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); onEdit?.(yard, e); }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600" title="Edit">
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2 border-t border-slate-100 pt-1.5">
                        <span className="text-xs text-slate-500">
                          <Home className="h-3 w-3 inline mr-0.5 text-teal-400" />
                          <span className="font-semibold text-slate-700">{count}</span> hive{count !== 1 ? 's' : ''}
                        </span>
                        <SaveApiaryOfflineButton yard={yard} hives={hives.filter(h => h.yard === yard.name)} />
                        <span className="text-[10px] text-slate-400 group-hover:text-teal-600 font-medium flex items-center gap-0.5 transition-colors">
                          View hives <ChevronRight className="h-3 w-3" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiariesList;
